import type { CircuitFile, SerializedElement } from './storage';
import { FORMAT, VERSION } from './storage';
import type { Props } from '../engine/element';
import type { SubcktDef, PortInfo } from '../engine/subckt';

type Def = [string, number, number, number, number, Props?];

function toElements(defs: Def[], prefix = 'x'): SerializedElement[] {
  return defs.map(([type, x1, y1, x2, y2, props], i) => ({
    id: `${prefix}${i + 1}`,
    type, x1, y1, x2, y2,
    props: props ?? {},
  }));
}

function make(name: string, defs: Def[], subcircuits: SubcktDef[] = []): CircuitFile {
  return {
    format: FORMAT, version: VERSION, name, savedAt: new Date().toISOString(),
    elements: toElements(defs), subcircuits,
  };
}

/** 예제용 모듈 정의 (포트 순서: 왼쪽 위→아래, 오른쪽 위→아래) */
function moduleDef(id: string, name: string, color: string, defs: Def[]): SubcktDef {
  const ports = defs
    .filter(([t]) => t === 'port')
    .map(([, x, y, , , p]) => ({ name: String(p?.name), side: (p?.side === 'right' ? 'right' : 'left') as PortInfo['side'], x, y }));
  const order = (side: string) => ports.filter((q) => q.side === side).sort((a, b) => a.y - b.y || a.x - b.x);
  return {
    id, name, version: 1, color,
    ports: [...order('left'), ...order('right')].map(({ name: n, side }) => ({ name: n, side })),
    elements: toElements(defs, `${id}-`),
  };
}

/** 보조배터리 모듈: 충전 모듈 + 리튬 셀 + 5V 승압 */
export const POWERBANK_DEF = moduleDef('ex-powerbank', '보조배터리', '#7c5cff', [
  ['port', 0, 3, 0, 4, { name: 'IN+', side: 'left' }],
  ['port', 0, 9, 0, 10, { name: 'IN−', side: 'left' }],
  ['wire', 0, 3, 8, 3],
  ['wire', 8, 3, 8, 5],
  ['wire', 0, 9, 8, 9],
  ['wire', 8, 9, 8, 7],
  ['charger', 8, 6, 12, 6, { chargeCurrent: 1, cvVoltage: 4.2, termination: 0.1, minInput: 4.4 }],
  ['wire', 12, 5, 12, 3],
  ['wire', 12, 3, 16, 3],
  ['wire', 12, 7, 12, 9],
  ['wire', 12, 9, 16, 9],
  ['liion', 16, 9, 16, 3, { capacity: 5000, internalR: 0.04, initialSoc: 0.5 }],
  ['wire', 16, 3, 20, 3],
  ['wire', 20, 3, 20, 5],
  ['wire', 16, 9, 20, 9],
  ['wire', 20, 9, 20, 7],
  ['dcdc', 20, 6, 24, 6, { vout: 5, imax: 2.4, efficiency: 0.9, uvlo: 2.9 }],
  ['wire', 24, 5, 24, 3],
  ['wire', 24, 3, 28, 3],
  ['wire', 24, 7, 24, 9],
  ['wire', 24, 9, 28, 9],
  ['port', 28, 3, 28, 4, { name: 'OUT+', side: 'right' }],
  ['port', 28, 9, 28, 10, { name: 'OUT−', side: 'right' }],
]);

/** 휴대폰 전원부: USB 충전 → 팩 레일 ↔ 보호회로 ↔ 셀, 팩 레일 = 시스템 전원 */
export const PHONE_PMIC_DEF = moduleDef('ex-phone-power', '휴대폰 전원부', '#0ea5e9', [
  ['port', 0, 3, 0, 4, { name: 'USB+', side: 'left' }],
  ['port', 0, 9, 0, 10, { name: 'USB−', side: 'left' }],
  ['wire', 0, 3, 4, 3],
  ['wire', 4, 3, 4, 5],
  ['wire', 0, 9, 4, 9],
  ['wire', 4, 9, 4, 7],
  ['charger', 4, 6, 8, 6, { chargeCurrent: 1.5, cvVoltage: 4.2, termination: 0.1, minInput: 4.4 }],
  ['wire', 8, 5, 8, 3],
  ['wire', 8, 3, 14, 3],
  ['wire', 8, 7, 8, 9],
  ['wire', 8, 9, 14, 9],
  ['wire', 14, 3, 14, 5],
  ['wire', 14, 9, 14, 7],
  ['bms', 18, 6, 14, 6, { ovc: 4.3, uvd: 2.8, ocp: 5, flip: true }],
  ['wire', 18, 5, 18, 3],
  ['wire', 18, 3, 22, 3],
  ['wire', 18, 7, 18, 9],
  ['wire', 18, 9, 22, 9],
  ['liion', 22, 9, 22, 3, { capacity: 4000, internalR: 0.05, initialSoc: 0.25 }],
  ['wire', 14, 3, 14, 1],
  ['wire', 14, 1, 26, 1],
  ['wire', 14, 9, 14, 11],
  ['wire', 14, 11, 26, 11],
  ['port', 26, 1, 26, 2, { name: 'SYS+', side: 'right' }],
  ['port', 26, 11, 26, 12, { name: 'SYS−', side: 'right' }],
]);

export interface ExampleEntry {
  name: string;
  desc: string;
  file: CircuitFile;
}

export const EXAMPLES: ExampleEntry[] = [
  {
    name: '기본 직렬 회로',
    desc: '전지 · 스위치 · 전구. 스위치를 눌러 켜보세요',
    file: make('기본 직렬 회로', [
      ['wire', 4, 16, 4, 12],
      ['battery', 4, 12, 4, 8, { emf: 6, internalR: 0.05 }],
      ['wire', 4, 8, 4, 4],
      ['wire', 4, 4, 10, 4],
      ['switch', 10, 4, 14, 4, { closed: true }],
      ['wire', 14, 4, 18, 4],
      ['wire', 18, 4, 18, 8],
      ['lamp', 18, 8, 18, 12, { resistance: 10, nominalPower: 3.6 }],
      ['wire', 18, 12, 18, 16],
      ['wire', 18, 16, 4, 16],
      ['ground', 4, 16, 4, 17],
    ]),
  },
  {
    name: '병렬 회로',
    desc: '전구 두 개를 병렬로. 각각 다른 전류가 흐릅니다',
    file: make('병렬 회로', [
      ['wire', 4, 16, 4, 12],
      ['battery', 4, 12, 4, 8, { emf: 6, internalR: 0.05 }],
      ['wire', 4, 8, 4, 4],
      ['wire', 4, 4, 12, 4],
      ['wire', 12, 4, 20, 4],
      ['lamp', 12, 4, 12, 10, { resistance: 20, nominalPower: 1.8 }],
      ['wire', 12, 10, 12, 16],
      ['lamp', 20, 4, 20, 10, { resistance: 40, nominalPower: 0.9 }],
      ['wire', 20, 10, 20, 16],
      ['wire', 20, 16, 12, 16],
      ['wire', 12, 16, 4, 16],
      ['ground', 4, 16, 4, 17],
    ]),
  },
  {
    name: '분압기 + 전압계',
    desc: '10V 를 1kΩ : 2kΩ 로 나누면 6.67V',
    file: make('분압기', [
      ['wire', 4, 16, 4, 12],
      ['vsource', 4, 12, 4, 8, { voltage: 10, waveform: 'dc' }],
      ['wire', 4, 8, 4, 4],
      ['wire', 4, 4, 14, 4],
      ['resistor', 14, 4, 14, 9, { resistance: 1000 }],
      ['resistor', 14, 9, 14, 14, { resistance: 2000 }],
      ['wire', 14, 14, 14, 16],
      ['wire', 14, 16, 4, 16],
      ['wire', 14, 9, 22, 9],
      ['wire', 14, 14, 22, 14],
      ['voltmeter', 22, 9, 22, 14],
      ['ground', 4, 16, 4, 17],
    ]),
  },
  {
    name: 'RC 충전 회로',
    desc: '1kΩ + 1000µF → 시상수 1초. 스위치를 열었다 닫아보세요',
    file: make('RC 충전', [
      ['wire', 4, 16, 4, 12],
      ['vsource', 4, 12, 4, 8, { voltage: 5, waveform: 'dc' }],
      ['wire', 4, 8, 4, 4],
      ['wire', 4, 4, 10, 4],
      ['switch', 10, 4, 14, 4, { closed: true }],
      ['wire', 14, 4, 18, 4],
      ['wire', 18, 4, 18, 6],
      ['resistor', 18, 6, 18, 10, { resistance: 1000 }],
      ['wire', 18, 10, 18, 12],
      ['capacitor', 18, 12, 18, 16, { capacitance: 1e-3, initialV: 0 }],
      ['wire', 18, 16, 4, 16],
      ['wire', 18, 12, 26, 12],
      ['wire', 18, 16, 26, 16],
      ['voltmeter', 26, 12, 26, 16],
      ['ground', 4, 16, 4, 17],
    ]),
  },
  {
    name: 'LED + 전류제한 저항',
    desc: '5V 에 150Ω 를 달면 LED 에 약 20mA',
    file: make('LED 회로', [
      ['wire', 4, 16, 4, 12],
      ['battery', 4, 12, 4, 8, { emf: 5, internalR: 0.1 }],
      ['wire', 4, 8, 4, 4],
      ['wire', 4, 4, 10, 4],
      ['resistor', 10, 4, 14, 4, { resistance: 150 }],
      ['wire', 14, 4, 18, 4],
      ['wire', 18, 4, 18, 8],
      ['led', 18, 8, 18, 12, { vf: 2, nominalI: 0.02, color: '#ff4d4d' }],
      ['wire', 18, 12, 18, 16],
      ['wire', 18, 16, 4, 16],
      ['ground', 4, 16, 4, 17],
    ]),
  },
  {
    name: '배터리 방전 테스트',
    desc: '3.7V 2000mAh 셀에 1A 부하. 속도를 올리면 잔량이 줄어듭니다',
    file: make('보조배터리 방전', [
      ['wire', 4, 16, 4, 12],
      ['battery', 4, 12, 4, 8, { emf: 3.7, internalR: 0.08, capacity: 2000, initialSoc: 1 }],
      ['wire', 4, 8, 4, 4],
      ['wire', 4, 4, 9, 4],
      ['ammeter', 9, 4, 13, 4],
      ['wire', 13, 4, 18, 4],
      ['switch', 18, 4, 22, 4, { closed: true }],
      ['wire', 22, 4, 22, 8],
      ['resistor', 22, 8, 22, 12, { resistance: 3.7, ratedPower: 10 }],
      ['wire', 22, 12, 22, 16],
      ['wire', 22, 16, 4, 16],
      ['ground', 4, 16, 4, 17],
    ]),
  },
  {
    name: '다이오드 반파 정류',
    desc: '사인파를 다이오드로 정류. 속도를 낮추고 파형을 보세요',
    file: make('반파 정류', [
      ['wire', 4, 16, 4, 12],
      ['vsource', 4, 12, 4, 8, { voltage: 5, waveform: 'sine', frequency: 5 }],
      ['wire', 4, 8, 4, 4],
      ['wire', 4, 4, 10, 4],
      ['diode', 10, 4, 14, 4, { is: 1e-14, n: 1 }],
      ['wire', 14, 4, 18, 4],
      ['wire', 18, 4, 18, 8],
      ['resistor', 18, 8, 18, 12, { resistance: 1000 }],
      ['wire', 18, 12, 18, 16],
      ['wire', 18, 16, 4, 16],
      ['ground', 4, 16, 4, 17],
    ]),
  },
  {
    name: '트랜지스터 스위치',
    desc: '작은 베이스 전류로 큰 컬렉터 전류를 켭니다. 스위치를 눌러보세요',
    file: make('트랜지스터 스위치', [
      ['wire', 4, 16, 4, 12],
      ['battery', 4, 12, 4, 8, { emf: 5, internalR: 0.05 }],
      ['wire', 4, 8, 4, 4],
      ['wire', 4, 4, 10, 4],
      ['wire', 10, 4, 16, 4],
      ['switch', 10, 4, 10, 8, { closed: false }],
      ['resistor', 10, 8, 10, 12, { resistance: 10000 }],
      ['wire', 10, 12, 14, 12],
      ['npn', 14, 12, 16, 12, { beta: 100 }],
      ['resistor', 16, 4, 16, 7, { resistance: 150 }],
      ['led', 16, 7, 16, 11, { vf: 2, nominalI: 0.02, color: '#4ade80' }],
      ['wire', 16, 13, 16, 16],
      ['wire', 16, 16, 4, 16],
      ['ground', 4, 16, 4, 17],
    ]),
  },
  {
    name: '연산증폭기 반전 증폭',
    desc: '입력 1V 사인파를 −3배로. 증폭기를 클릭해 파형을 보세요',
    file: make('반전 증폭기', [
      ['vsource', 4, 14, 4, 10, { voltage: 1, waveform: 'sine', frequency: 1 }],
      ['wire', 4, 10, 4, 7],
      ['resistor', 4, 7, 8, 7, { resistance: 1000 }],
      ['wire', 8, 7, 12, 7],
      ['wire', 12, 7, 12, 4],
      ['resistor', 12, 4, 16, 4, { resistance: 3000 }],
      ['wire', 16, 4, 16, 8],
      ['opamp', 12, 8, 16, 8, { gain: 1e5, vpos: 12, vneg: -12 }],
      ['wire', 12, 9, 12, 14],
      ['wire', 12, 14, 4, 14],
      ['wire', 16, 8, 20, 8],
      ['resistor', 20, 8, 20, 14, { resistance: 10000 }],
      ['wire', 20, 14, 12, 14],
      ['ground', 4, 14, 4, 15],
    ]),
  },
  {
    name: '보조배터리',
    desc: 'USB → 충전 모듈 → 리튬 셀 → 5V 승압 → 부하. USB 스위치를 끊어보세요 (속도↑)',
    file: make('보조배터리', [
      ['usb', 2, 9, 2, 3, { emf: 5, internalR: 0.2 }],
      ['switch', 2, 3, 6, 3, { closed: true }],
      ['wire', 6, 3, 8, 3],
      ['wire', 8, 3, 8, 5],
      ['wire', 2, 9, 8, 9],
      ['wire', 8, 9, 8, 7],
      ['charger', 8, 6, 12, 6, { chargeCurrent: 1, cvVoltage: 4.2, termination: 0.1, minInput: 4.4 }],
      ['wire', 12, 5, 12, 3],
      ['wire', 12, 3, 16, 3],
      ['wire', 12, 7, 12, 9],
      ['wire', 12, 9, 16, 9],
      ['liion', 16, 9, 16, 3, { capacity: 2000, internalR: 0.05, initialSoc: 0.3 }],
      ['wire', 16, 3, 20, 3],
      ['wire', 20, 3, 20, 5],
      ['wire', 16, 9, 20, 9],
      ['wire', 20, 9, 20, 7],
      ['dcdc', 20, 6, 24, 6, { vout: 5, imax: 2, efficiency: 0.9, uvlo: 2.9 }],
      ['wire', 24, 5, 24, 3],
      ['switch', 24, 3, 28, 3, { closed: true }],
      ['wire', 24, 7, 24, 9],
      ['wire', 24, 9, 28, 9],
      ['resistor', 28, 3, 28, 9, { resistance: 10, ratedPower: 5 }],
      ['wire', 28, 3, 32, 3],
      ['wire', 28, 9, 32, 9],
      ['voltmeter', 32, 3, 32, 9],
      ['ground', 2, 9, 2, 10],
    ]),
  },
  {
    name: '보조배터리 (모듈)',
    desc: '보조배터리 회로를 부품 하나로 묶었어요. 더블클릭하면 안이 보여요',
    file: make('보조배터리 (모듈)', [
      ['usb', 2, 9, 2, 3, { emf: 5, internalR: 0.2 }],
      ['switch', 2, 3, 6, 3, { closed: true }],
      ['wire', 6, 3, 8, 3],
      ['wire', 8, 3, 8, 5],
      ['wire', 2, 9, 8, 9],
      ['wire', 8, 9, 8, 7],
      ['subckt', 8, 6, 14, 6, { defId: POWERBANK_DEF.id, name: POWERBANK_DEF.name, ports: JSON.stringify(POWERBANK_DEF.ports), color: POWERBANK_DEF.color ?? '#7c5cff' }],
      ['wire', 14, 5, 14, 3],
      ['switch', 14, 3, 18, 3, { closed: true }],
      ['wire', 14, 7, 14, 9],
      ['wire', 14, 9, 18, 9],
      ['cpload', 18, 3, 18, 9, { preset: 'custom', power: 5, vmin: 4 }],
      ['wire', 18, 3, 22, 3],
      ['wire', 18, 9, 22, 9],
      ['voltmeter', 22, 3, 22, 9],
      ['ground', 2, 9, 2, 10],
    ], [POWERBANK_DEF]),
  },
  {
    name: '휴대폰 (모듈)',
    desc: '전원부 모듈 + 화면·CPU·통신 부하. 충전기 스위치와 게임(CPU) 스위치를 눌러보세요 (속도↑)',
    file: make('휴대폰', [
      ['usb', 2, 9, 2, 3, { emf: 5, internalR: 0.2 }],
      ['switch', 2, 3, 6, 3, { closed: false }],
      ['wire', 6, 3, 8, 3],
      ['wire', 8, 3, 8, 5],
      ['wire', 2, 9, 8, 9],
      ['wire', 8, 9, 8, 7],
      ['subckt', 8, 6, 14, 6, { defId: PHONE_PMIC_DEF.id, name: PHONE_PMIC_DEF.name, ports: JSON.stringify(PHONE_PMIC_DEF.ports), color: PHONE_PMIC_DEF.color ?? '#0ea5e9' }],
      ['wire', 14, 5, 14, 3],
      ['wire', 14, 3, 18, 3],
      ['wire', 18, 3, 24, 3],
      ['wire', 24, 3, 30, 3],
      ['wire', 14, 7, 14, 9],
      ['wire', 14, 9, 18, 9],
      ['wire', 18, 9, 24, 9],
      ['wire', 24, 9, 30, 9],
      ['switch', 18, 3, 18, 5, { closed: true }],
      ['cpload', 18, 5, 18, 9, { preset: 'screen', vmin: 3.0 }],
      ['switch', 24, 3, 24, 5, { closed: false }],
      ['cpload', 24, 5, 24, 9, { preset: 'cpu-max', vmin: 3.0 }],
      ['switch', 30, 3, 30, 5, { closed: true }],
      ['cpload', 30, 5, 30, 9, { preset: 'radio', vmin: 3.0 }],
      ['wire', 30, 3, 34, 3],
      ['wire', 30, 9, 34, 9],
      ['voltmeter', 34, 3, 34, 9],
      ['ground', 2, 9, 2, 10],
    ], [PHONE_PMIC_DEF]),
  },
  {
    name: '발열: 저항이 타요',
    desc: '9V 에 100Ω = 0.81W. 1W 저항은 뜨겁기만, 0.25W 저항은 타요. LED 스위치는… (초기화로 복구)',
    file: make('발열 실험', [
      ['battery', 2, 12, 2, 6, { emf: 9, internalR: 0.5 }],
      ['wire', 2, 6, 2, 4],
      ['wire', 2, 12, 2, 14],
      ['wire', 2, 4, 8, 4],
      ['wire', 8, 4, 14, 4],
      ['wire', 14, 4, 20, 4],
      ['wire', 2, 14, 8, 14],
      ['wire', 8, 14, 14, 14],
      ['wire', 14, 14, 20, 14],
      ['switch', 8, 4, 8, 7, { closed: true }],
      ['resistor', 8, 7, 8, 11, { resistance: 100, ratedPower: 1 }],
      ['wire', 8, 11, 8, 14],
      ['switch', 14, 4, 14, 7, { closed: false }],
      ['resistor', 14, 7, 14, 11, { resistance: 100, ratedPower: 0.25 }],
      ['wire', 14, 11, 14, 14],
      ['switch', 20, 4, 20, 7, { closed: false }],
      ['led', 20, 7, 20, 11, { vf: 2, nominalI: 0.02, color: '#60a5fa' }],
      ['wire', 20, 11, 20, 14],
      ['ground', 2, 14, 2, 15],
    ]),
  },
  {
    name: '논리: 반가산기',
    desc: '두 입력을 클릭해 바꿔보세요. XOR = 합, AND = 올림',
    file: make('반가산기', [
      ['logicin', 6, 4, 5, 4, { on: false }],
      ['logicin', 6, 12, 5, 12, { on: false }],
      ['wire', 6, 4, 10, 4],
      ['wire', 10, 4, 14, 4],
      ['wire', 10, 4, 10, 10],
      ['wire', 10, 10, 14, 10],
      ['wire', 6, 12, 8, 12],
      ['wire', 8, 12, 14, 12],
      ['wire', 8, 12, 8, 6],
      ['wire', 8, 6, 14, 6],
      ['xor', 14, 5, 18, 5],
      ['and', 14, 11, 18, 11],
      ['wire', 18, 5, 21, 5],
      ['wire', 18, 11, 21, 11],
      ['logicout', 21, 5, 22, 5],
      ['logicout', 21, 11, 22, 11],
    ]),
  },
  {
    name: '논리: 플립플롭 ÷2',
    desc: 'Q̄ 를 D 로 되먹이면 클럭 두 번에 한 번씩 바뀌어요 (분주기)',
    file: make('플립플롭 분주기', [
      ['clock', 6, 8, 5, 8, { frequency: 1 }],
      ['wire', 6, 8, 8, 8],
      ['wire', 8, 8, 12, 8],
      ['wire', 8, 8, 8, 4],
      ['wire', 8, 4, 20, 4],
      ['logicout', 20, 4, 21, 4],
      ['dff', 12, 7, 16, 7],
      ['wire', 16, 8, 18, 8],
      ['wire', 18, 8, 18, 11],
      ['wire', 18, 11, 10, 11],
      ['wire', 10, 11, 10, 6],
      ['wire', 10, 6, 12, 6],
      ['wire', 16, 6, 20, 6],
      ['logicout', 20, 6, 21, 6],
    ]),
  },
  {
    name: 'MCU: 깜빡이 + 숨쉬기',
    desc: '마이크로컨트롤러 코드로 LED 제어. 칩을 클릭해 코드를 고쳐보세요',
    file: make('MCU 깜빡이', [
      ['battery', 2, 5, 2, 3, { emf: 5, internalR: 0.05 }],
      ['wire', 2, 3, 8, 3],
      ['wire', 2, 5, 8, 5],
      ['ground', 2, 5, 2, 6],
      ['mcu', 8, 6, 14, 6, { tickMs: 1, code: `// P2: 0.5초마다 깜빡, P5: PWM 으로 숨쉬듯 밝기 변화
function setup() {
  pinMode(2, 'out');
  pinMode(5, 'out');
  print('깜빡이 + 숨쉬기 시작!');
}

function loop() {
  const t = millis();
  digitalWrite(2, Math.floor(t / 500) % 2);
  analogWrite(5, 127 + 127 * Math.sin(t / 300));
}
` }],
      ['resistor', 14, 3, 18, 3, { resistance: 150 }],
      ['led', 18, 3, 22, 3, { vf: 2, color: '#ff4d4d' }],
      ['ground', 22, 3, 22, 4],
      ['resistor', 14, 9, 18, 9, { resistance: 150 }],
      ['led', 18, 9, 22, 9, { vf: 2, color: '#60a5fa' }],
      ['ground', 22, 9, 22, 10],
    ]),
  },
  {
    name: 'MCU: 배터리 잔량 표시기',
    desc: '셀 전압을 읽어 LED 3칸으로. 버튼을 누르고 있으면 표시 (속도↑ 로 방전 관찰)',
    file: make('배터리 잔량 표시기', [
      ['usb', 2, 7, 2, 5, { emf: 5, internalR: 0.2 }],
      ['wire', 2, 5, 6, 5],
      ['wire', 6, 5, 10, 5],
      ['wire', 2, 7, 10, 7],
      ['ground', 2, 7, 2, 8],
      ['mcu', 10, 8, 16, 8, { tickMs: 5, code: `// P0: 셀 전압 측정 · P1: 버튼 (누르면 1) · P2~P4: 잔량 LED
function setup() {
  pinMode(0, 'in');
  pinMode(1, 'in');
  for (const p of [2, 3, 4]) pinMode(p, 'out');
}

let last = -1;
function loop() {
  const v = readVoltage(0);
  const level = v > 3.95 ? 3 : v > 3.7 ? 2 : v > 3.4 ? 1 : 0;
  const pressed = digitalRead(1) === 1;
  // 버튼을 누르고 있거나, 1칸 이하면 경고로 빨간불 깜빡
  const warn = level <= 1 && Math.floor(millis() / 300) % 2 === 0;
  digitalWrite(2, (pressed && level >= 1) || warn ? 1 : 0);
  digitalWrite(3, pressed && level >= 2 ? 1 : 0);
  digitalWrite(4, pressed && level >= 3 ? 1 : 0);
  if (level !== last) {
    print('셀 ' + v.toFixed(2) + 'V → ' + level + '칸');
    last = level;
  }
}
` }],
      ['wire', 6, 5, 6, 11],
      ['button', 6, 11, 10, 11],
      ['resistor', 10, 11, 10, 14, { resistance: 10000 }],
      ['ground', 10, 14, 10, 15],
      ['liion', 4, 15, 4, 11, { capacity: 300, internalR: 0.05, initialSoc: 0.95 }],
      ['wire', 4, 11, 4, 9],
      ['wire', 4, 9, 10, 9],
      ['ground', 4, 15, 4, 16],
      ['wire', 4, 11, 0, 11],
      ['switch', 0, 11, 0, 14, { closed: true }],
      ['resistor', 0, 14, 0, 17, { resistance: 8, ratedPower: 5 }],
      ['ground', 0, 17, 0, 18],
      ['resistor', 16, 5, 20, 5, { resistance: 150 }],
      ['led', 20, 5, 24, 5, { vf: 2, color: '#ef4444' }],
      ['ground', 24, 5, 24, 6],
      ['resistor', 16, 7, 20, 7, { resistance: 150 }],
      ['led', 20, 7, 24, 7, { vf: 2.1, color: '#facc15' }],
      ['ground', 24, 7, 24, 8],
      ['resistor', 16, 9, 20, 9, { resistance: 150 }],
      ['led', 20, 9, 24, 9, { vf: 2.2, color: '#4ade80' }],
      ['ground', 24, 9, 24, 10],
    ]),
  },
];
